import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { GridColDef } from "@mui/x-data-grid"

import Table from "@/components/table/Table"
import Icon from "@/components/icon/Icon"
import Chip from "@/components/chip/Chip"
import { CalculatedMetric } from "@/types/setting"
import { colors } from "@/components/colors"
import { getTextWidth } from "@/app/report/common/tools"

interface Props {
  data: CalculatedMetric[]
  selectedId: number | string | null // 새 메트릭은 name으로 식별
  onSelect: (metric: CalculatedMetric) => void
  isLoading: boolean
}

const MIN_TABLE_WIDTH = 240
const MAX_TABLE_WIDTH = 412
const IN_USE_CHIP_WIDTH = 50 // "In Use" 칩 너비 + gap
const ARROW_ICON_WIDTH = 28 // 화살표 아이콘 너비 + padding

export default function MetricTable({ data, selectedId, onSelect, isLoading }: Props) {
  const { t } = useTranslation()

  // 테이블 너비 계산 (가장 긴 이름 기준, 메트릭이 없으면 전체 너비)
  const tableWidth = useMemo(() => {
    // 메트릭이 없으면 width 제한 없이 전체 너비 사용
    if (data.length === 0) return "100%"

    let maxWidth = 0
    for (const metric of data) {
      const nameWidth = getTextWidth(metric.name)
      const hasChip = metric.includedTemplates && metric.includedTemplates.length > 0
      const totalWidth = nameWidth + (hasChip ? IN_USE_CHIP_WIDTH : 0) + ARROW_ICON_WIDTH
      maxWidth = Math.max(maxWidth, totalWidth)
    }

    return Math.min(Math.max(maxWidth, MIN_TABLE_WIDTH), MAX_TABLE_WIDTH)
  }, [data])

  // row ID 계산 (idfMetric 또는 name)
  const getRowId = (row: CalculatedMetric) => row.idfMetric ?? row.name

  // 선택 여부 확인
  const isSelected = (row: CalculatedMetric) => {
    const rowId = getRowId(row)
    return rowId === selectedId
  }

  const columns: GridColDef<CalculatedMetric>[] = [
    {
      field: "name",
      renderHeader: () => t("name"),
      flex: 1,
      minWidth: 240,
      renderCell: (params) => {
        const isUsed = params.row.includedTemplates && params.row.includedTemplates.length > 0
        const selected = isSelected(params.row)
        return (
          <div className="metric-name-cell">
            <div className="name-with-chip">
              <span className="name-text">{params.row.name}</span>
              {isUsed && <Chip type="default-small" label={t("inUse")} className="in-use-chip" />}
            </div>
            {selected && <Icon type="arrow-right" size={16} color={colors.neutral.grey[600]} />}
          </div>
        )
      },
    },
  ]

  return (
    <Table
      className="MetricTableCont"
      columns={columns}
      rows={data}
      getRowId={getRowId}
      rowHeight={52}
      loading={isLoading}
      disableColumnResize
      scrollbarSize={0}
      hideFooter
      rowSelectionModel={{
        type: "include",
        ids: new Set(selectedId !== null ? [selectedId] : []),
      }}
      onRowSelectionModelChange={(model) => {
        if (model.ids.size > 0) {
          const id = Array.from(model.ids)[0]
          // id가 숫자면 idfMetric으로, 문자열이면 name으로 찾기
          const selectedMetric = data.find((m) =>
            typeof id === "number" ? m.idfMetric === id : m.name === id,
          )
          if (selectedMetric) {
            onSelect(selectedMetric)
          }
        }
      }}
      localeText={{
        noRowsLabel: t("messages.noDataAvailable"),
      }}
      sx={{
        width: tableWidth,
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "inherit",
          cursor: "pointer",
        },
      }}
    />
  )
}
